import type { RefObject } from 'react';
import { useRef, useState, useCallback, useEffect } from 'react';
import { invokeEdgeFunction } from '../lib/supabase';

type SpeakResult = 'completed' | 'interrupted';

/** Accumulate this many samples before starting playback (~50ms at 24kHz) */
const JITTER_BUFFER_SAMPLES = 1200;

interface UseTtsPlayerReturn {
  speak: (text: string, onPlaybackStarted?: () => void) => Promise<SpeakResult>;
  stop: () => void;
  warmup: () => Promise<void>;
  isPlaying: boolean;
  analyserRef: RefObject<AnalyserNode | null>;
}

export function useTtsPlayer(): UseTtsPlayerReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const resolveRef = useRef<((result: SpeakResult) => void) | null>(null);
  const workletReadyRef = useRef(false);

  const ensureAudioContext = useCallback(async () => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      // 24kHz matches OpenAI PCM output — avoids resampling
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      workletReadyRef.current = false;
    }

    const ctx = audioContextRef.current;

    // Register worklet module once per context
    if (!workletReadyRef.current) {
      await ctx.audioWorklet.addModule('/pcm-playback-processor.js');
      workletReadyRef.current = true;
    }

    return ctx;
  }, []);

  /** Pre-warm AudioContext + worklet registration so speak() is instant */
  const warmup = useCallback(async () => {
    try {
      await ensureAudioContext();
    } catch {
      // Non-critical — speak() will retry
    }
  }, [ensureAudioContext]);

  const stop = useCallback(() => {
    // Cancel the fetch stream reader
    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {});
      readerRef.current = null;
    }

    // Tell the worklet to stop immediately
    if (workletNodeRef.current) {
      try {
        workletNodeRef.current.port.postMessage('stop');
      } catch {
        // Port may be closed
      }
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    setIsPlaying(false);
    if (resolveRef.current) {
      resolveRef.current('interrupted');
      resolveRef.current = null;
    }
  }, []);

  const speak = useCallback(async (text: string, onPlaybackStarted?: () => void): Promise<SpeakResult> => {
    // Stop any current playback
    stop();

    const ctx = await ensureAudioContext();

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    // Set up audio graph: workletNode -> analyser -> destination
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.8;
    analyserRef.current = analyser;

    const workletNode = new AudioWorkletNode(ctx, 'pcm-playback-processor', {
      outputChannelCount: [1],
    });
    workletNodeRef.current = workletNode;

    workletNode.connect(analyser);
    analyser.connect(ctx.destination);

    // Fetch TTS audio stream
    const response = await invokeEdgeFunction('tts', {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      throw new Error(`TTS request failed: HTTP ${response.status}`);
    }

    if (!response.body) {
      throw new Error('TTS response has no body');
    }

    const reader = response.body.getReader();
    readerRef.current = reader;

    return new Promise<SpeakResult>((resolve) => {
      resolveRef.current = resolve;
      setIsPlaying(true);

      // Listen for 'ended' from the worklet
      workletNode.port.onmessage = (e) => {
        if (e.data === 'ended') {
          workletNode.disconnect();
          analyser.disconnect();
          workletNodeRef.current = null;
          analyserRef.current = null;
          setIsPlaying(false);
          if (resolveRef.current) {
            resolveRef.current('completed');
            resolveRef.current = null;
          }
        }
      };

      // Pump loop: read PCM chunks, handle byte alignment, convert to Float32
      let leftoverByte: number | null = null;
      let jitterBuffer: Float32Array | null = new Float32Array(JITTER_BUFFER_SAMPLES);
      let jitterFilled = 0;
      let jitterFlushed = false;

      const pump = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            // --- Handle byte alignment for Int16 PCM ---
            // Stream chunks can split mid-sample (odd byte count).
            // Prepend any leftover byte from the previous chunk.
            let bytes: Uint8Array;
            if (leftoverByte !== null) {
              bytes = new Uint8Array(1 + value.byteLength);
              bytes[0] = leftoverByte;
              bytes.set(value, 1);
              leftoverByte = null;
            } else {
              bytes = value;
            }

            // If odd number of bytes, save the last byte for next chunk
            if (bytes.byteLength % 2 !== 0) {
              leftoverByte = bytes[bytes.byteLength - 1];
              bytes = bytes.subarray(0, bytes.byteLength - 1);
            }

            if (bytes.byteLength === 0) continue;

            // Copy into an aligned buffer so Int16Array view works correctly
            const aligned = new ArrayBuffer(bytes.byteLength);
            new Uint8Array(aligned).set(bytes);
            const int16View = new Int16Array(aligned);

            const float32 = new Float32Array(int16View.length);
            for (let i = 0; i < int16View.length; i++) {
              float32[i] = int16View[i] / 32768;
            }

            if (!jitterFlushed && jitterBuffer) {
              // Fill jitter buffer first to avoid underrun glitches
              const spaceLeft = JITTER_BUFFER_SAMPLES - jitterFilled;
              const toCopy = Math.min(spaceLeft, float32.length);
              jitterBuffer.set(float32.subarray(0, toCopy), jitterFilled);
              jitterFilled += toCopy;

              if (jitterFilled >= JITTER_BUFFER_SAMPLES) {
                // Flush the jitter buffer — audio will start playing now
                workletNode.port.postMessage(jitterBuffer);
                jitterFlushed = true;
                jitterBuffer = null;
                onPlaybackStarted?.();

                // Send any remaining samples from this chunk
                if (toCopy < float32.length) {
                  workletNode.port.postMessage(float32.subarray(toCopy));
                }
              }
            } else {
              workletNode.port.postMessage(float32);
            }
          }

          // If we never filled the jitter buffer, flush what we have
          if (!jitterFlushed && jitterBuffer && jitterFilled > 0) {
            workletNode.port.postMessage(jitterBuffer.subarray(0, jitterFilled));
            onPlaybackStarted?.();
          }

          // Signal that no more data is coming
          workletNode.port.postMessage('finish');
        } catch {
          // Reader was cancelled (stop() called) or network error — ignore
        }
      };

      pump();
    });
  }, [stop, ensureAudioContext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {});
        readerRef.current = null;
      }
      if (workletNodeRef.current) {
        try {
          workletNodeRef.current.port.postMessage('stop');
        } catch { /* */ }
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
        workletReadyRef.current = false;
      }
    };
  }, []);

  return { speak, stop, warmup, isPlaying, analyserRef };
}
